import isEqual from 'lodash/isEqual';
import { FormField } from '@/modules/forms/formConfig';
import { AnswerReviewEditor, AnswerReviewValue } from './AnswerReviewWorkbench.types';
export class FormSubmissionAdapterError extends Error {}
export function getEditor(field: FormField): AnswerReviewEditor {
  const options = field.options ?? [];
  switch (field.fieldType) {
    case 'shortText':
      return {
        kind: 'text',
        maxLength: field.validation?.maxLength
      };
    case 'longText':
      return {
        kind: 'text',
        multiline: true,
        maxLength: field.validation?.maxLength
      };
    case 'number':
      return {
        kind: 'number'
      };
    case 'date':
      return {
        kind: 'date'
      };
    case 'singleSelect':
      return {
        kind: 'singleSelect',
        options
      };
    case 'multiSelect':
      return {
        kind: 'multiSelect',
        options
      };
    case 'object':
    case 'array':
      throw new FormSubmissionAdapterError('A container requires a typed editor.');
  }
}
export function parseAnswerValue(rawValue: unknown, field: FormField, allowNull: boolean): AnswerReviewValue {
  if (rawValue === null || rawValue === undefined) {
    if (allowNull) return null;
    throw new FormSubmissionAdapterError(`Field "${field.id}" must have a value.`);
  }
  const isKnownOption = (value: string) => field.options?.some(option => option.value === value) ?? false;
  switch (field.fieldType) {
    case 'shortText':
    case 'longText':
      if (typeof rawValue === 'string') return rawValue;
      break;
    case 'number':
      if (typeof rawValue === 'number' && Number.isFinite(rawValue)) return rawValue;
      break;
    case 'date':
      if (typeof rawValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawValue)) return rawValue;
      break;
    case 'singleSelect':
      if (typeof rawValue === 'string' && isKnownOption(rawValue)) return rawValue;
      break;
    case 'multiSelect':
      if (Array.isArray(rawValue) && rawValue.every((item): item is string => typeof item === 'string' && isKnownOption(item))) return rawValue;
      break;
  }
  throw new FormSubmissionAdapterError(`Field "${field.id}" has an invalid answer value.`);
}
function hasValue(value: AnswerReviewValue): boolean {
  if (value === null || value === '') return false;
  return !Array.isArray(value) || value.length > 0;
}
export function isChildActive(field: FormField, parent: FormField, parentValue: AnswerReviewValue): boolean {
  const condition = field.onlyActiveWhen;
  // Canonical forms can contain unconditional nested fields.
  if (!condition) return true;
  if (condition.fieldId !== parent.id) throw new FormSubmissionAdapterError(`Nested field "${field.id}" does not depend on its parent.`);
  const candidateValue: unknown = condition.candidateValue;
  switch (condition.operator) {
    case 'equals':
      return parentValue !== null && isEqual(parentValue, candidateValue);
    case 'notEquals':
      return parentValue !== null && !isEqual(parentValue, candidateValue);
    case 'answerInSelection':
      return Array.isArray(parentValue) && typeof candidateValue === 'string' && parentValue.includes(candidateValue);
    case 'answerNotInSelection':
      return Array.isArray(parentValue) && typeof candidateValue === 'string' && !parentValue.includes(candidateValue);
    case 'hasValue':
      return hasValue(parentValue);
    case 'doesNotHaveValue':
      return !hasValue(parentValue);
    default:
      throw new FormSubmissionAdapterError(`Nested field "${field.id}" has an unknown activation operator.`);
  }
}